import Modal from '@components/Modal';
import Button from '@components/Button';
import { useRef, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Icon from '@material-ui/core/Icon';
import NumberFormat from 'react-number-format';
import Alert from '@material-ui/lab/Alert';
import { apiClient } from '@services/api';

/**
 * Solicitação de estorno a partir do Fluxo de Caixa.
 * Passo 1: tipo (Integral/Parcial), valor (se parcial), motivo.
 * Passo 2: confirmação. Só ABRE o pedido (status aguardando liberação) — o estorno real
 * acontece na tela de Reembolsos, liberado pelo e-mail oposto (dupla custódia).
 */
const RefundModal = (props) => {
  const ref = useRef(null);
  const history = useHistory();
  const paid = Number(props.payment?.customer_paid_value ?? props.payment?.total ?? 0);

  const [step, setStep] = useState(1);
  const [type, setType] = useState('total');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  // Pedido pra essa OR já existe aguardando aprovação (outro consultor pediu antes) —
  // backend agora trava por sale_id, não só pay_id (2026-08-15). Em vez de só mostrar o
  // erro genérico, oferece ir direto pra tela de Reembolsos ver o pedido já aberto.
  const [duplicate, setDuplicate] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (props.open) { setStep(1); setType('total'); setValue(''); setReason(''); setError(null); setDuplicate(null); setLoading(false); }
  }, [props.open]);

  const brl = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const refundValue = type === 'partial' ? Number(value || 0) : paid;

  const goConfirm = () => {
    setError(null);
    if (!reason || reason.trim().length < 3) return setError('Informe o motivo do estorno.');
    if (type === 'partial') {
      if (!value || Number(value) <= 0) return setError('Informe o valor do estorno parcial.');
      if (Number(value) > paid) return setError('O valor não pode ser maior que o valor pago (' + brl(paid) + ').');
    }
    setStep(2);
  };

  const submit = () => {
    setLoading(true); setError(null); setDuplicate(null);
    apiClient.httpInstance.post('/refunds', {
      sale_payment_id: props.payment?.id,
      sale_id: props.payment?.sale_id,
      type,
      value: type === 'partial' ? Number(value) : undefined,
      reason: reason.trim(),
    }).then(() => {
      props.close();
      if (props.onSubmitted) props.onSubmitted();
    }).catch((e) => {
      setLoading(false);
      if (e?.response?.data?.duplicate) {
        setDuplicate(e.response.data);
      } else {
        setError(e?.response?.data?.message || 'Não foi possível abrir o pedido de estorno.');
      }
    });
  };

  const goToReembolsos = () => {
    props.close();
    history.push('/admin/reembolsos');
  };

  return <Modal open={props.open} close={props.close} style={{ width: '100%', maxWidth: '460px' }}>
    <div className="flex justify-between font-poppins">
      <span className="mb-1 text-xl text-blue">Solicitar reembolso</span>
      <Icon ref={ref} className="cursor-pointer" onClick={props.close}>close</Icon>
    </div>

    {props.payment && (
      <small className="text-gray">
        {props.payment.customer_name} &nbsp;·&nbsp; {props.payment.sale_references || props.payment.references} &nbsp;·&nbsp; pago {brl(paid)}
      </small>
    )}

    {step === 1 && (
      <div className="mt-4">
        <label className="text-sm text-blue">Tipo de estorno</label>
        <div className="flex gap-2 mt-1 mb-3">
          {[['total', 'Integral'], ['partial', 'Parcial']].map(([v, label]) => (
            <button key={v} type="button" onClick={() => setType(v)}
              style={{
                flex: 1, padding: '10px', borderRadius: '8px', fontWeight: 600, cursor: 'pointer',
                border: type === v ? '2px solid #47B098' : '1px solid #cbd5e1',
                background: type === v ? '#e6f4f0' : '#fff',
                color: type === v ? '#0a5f4c' : '#475569',
              }}>
              {label}
            </button>
          ))}
        </div>

        {type === 'partial' && (
          <div className="mb-3">
            <label className="text-sm text-blue">Valor do estorno</label>
            <NumberFormat
              value={value}
              thousandSeparator="." decimalSeparator="," prefix="R$ "
              decimalScale={2} fixedDecimalScale allowNegative={false}
              onValueChange={(v) => setValue(v.floatValue ?? '')}
              className="w-full mt-1 p-2 border rounded"
              placeholder={'Máx. ' + brl(paid)}
            />
          </div>
        )}

        <label className="text-sm text-blue">Motivo</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
          className="w-full mt-1 p-2 border rounded" placeholder="Ex.: cliente desistiu do passeio..." />

        {error && <Alert className="mt-2" severity="error">{error}</Alert>}

        <div className="justify-end mt-4 md:flex w-100">
          <Button textcolor="#172B4D" variant="outlined" padding="10px 15px" margintop="0px" marginright="20px"
            width="120px" onClick={props.close} bgColor="transparent" border="1px solid #172B4D">Cancelar</Button>
          <Button type="button" textcolor="#fff" padding="10px 15px" margintop="0px" width="140px"
            onClick={goConfirm}>Continuar</Button>
        </div>
      </div>
    )}

    {step === 2 && (
      <div className="mt-4">
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '14px' }}>
          <p className="text-blue" style={{ fontWeight: 600 }}>
            Tem certeza que quer pedir o reembolso <b>({type === 'partial' ? 'Parcial' : 'Integral'})</b> de <b>{brl(refundValue)}</b>?
          </p>
          <small className="text-gray">
            O pedido vai para a tela de Reembolsos e só será estornado após a liberação do outro responsável (dupla custódia).
          </small>
        </div>

        {duplicate ? (
          <div className="mt-2" style={{ background: '#fff7ed', border: '1px solid #fdba74', borderRadius: 8, padding: 14 }}>
            <p className="text-blue" style={{ fontWeight: 700, marginBottom: 4 }}>⚠️ Essa OR já tem um pedido aberto</p>
            <p className="text-gray" style={{ fontSize: 13, lineHeight: 1.5 }}>{duplicate.message}</p>
          </div>
        ) : (
          error && <Alert className="mt-2" severity="error">{error}</Alert>
        )}

        <div className="justify-end mt-4 md:flex w-100">
          {duplicate ? (
            <>
              <Button textcolor="#172B4D" variant="outlined" padding="10px 15px" margintop="0px" marginright="20px"
                width="120px" onClick={props.close} bgColor="transparent" border="1px solid #172B4D">Fechar</Button>
              <Button type="button" textcolor="#fff" padding="10px 15px" margintop="0px" width="180px"
                onClick={goToReembolsos}>Ver reembolsos</Button>
            </>
          ) : (
            <>
              <Button textcolor="#172B4D" variant="outlined" padding="10px 15px" margintop="0px" marginright="20px"
                width="120px" onClick={() => setStep(1)} bgColor="transparent" border="1px solid #172B4D"
                disabled={loading}>Voltar</Button>
              <Button type="button" textcolor="#fff" padding="10px 15px" margintop="0px" width="160px"
                onClick={submit} disabled={loading}>{loading ? 'Enviando...' : 'Sim, confirmar'}</Button>
            </>
          )}
        </div>
      </div>
    )}
  </Modal>;
};

export default RefundModal;
